import React, { FC } from "react";

import { useTranslation } from "react-i18next";

import { DateFormat, PolicyholderIcon } from "@/components/atoms";
import { Variant } from "@/components/atoms/Drawable/PolicyholderIcon/PolicyholderIcon";
import { ProfileFormPanel } from "@/components/organisms";
import { Permissions } from "@/shared/domain";
import { useAuth } from "@/shared/hooks";

import { GeneralCompanyDetailsFormType, GeneralDetailsFormType } from "../../formType";
import { generalCompanyDetailsSchema, generalDetailsSchema, PersonalDetailsAreas } from "../../utils";
import { generalCompanyDetailsFormValidation, generalDetailsFormValidation } from "../../validations";
import { StyledDateActive, StyledPolicyholderChip, StyledProfileHeader, StyledProfileName } from "./styled";

type PolicyholderCardProps = {
    policyholderId: number;
    personalDetails: MyBrokerApi.PolicyHolderPersonalDetails;
    onSubmitPersonalDetails: (
        formValue: GeneralDetailsFormType | GeneralCompanyDetailsFormType,
        detailArea: PersonalDetailsAreas,
    ) => void;
    isLoadingSubmit: boolean;
};

const PolicyholderCard: FC<PolicyholderCardProps> = ({
    policyholderId,
    personalDetails,
    onSubmitPersonalDetails,
    isLoadingSubmit,
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const allowEditInformationDetails = user.hasPermission(Permissions.AccessEditInformationDetails, policyholderId);

    const { currentDetails, pendingDetails, isPhysicalPerson = true } = personalDetails || {};

    return (
        <ProfileFormPanel
            dataTestId="general_information_form"
            panelSchema={isPhysicalPerson ? generalDetailsSchema : generalCompanyDetailsSchema}
            isEditable={allowEditInformationDetails}
            readCustomSize
            currentDetails={{ ...personalDetails, ...currentDetails }}
            pendingDetails={pendingDetails}
            validationSchema={isPhysicalPerson ? generalDetailsFormValidation() : generalCompanyDetailsFormValidation()}
            isLoading={isLoadingSubmit}
            onSubmit={(formValue: GeneralDetailsFormType | GeneralCompanyDetailsFormType) => {
                onSubmitPersonalDetails(formValue, PersonalDetailsAreas.GENERAL_INFO);
            }}
        >
            <StyledProfileHeader>
                <PolicyholderIcon variant={Variant.Monochrome} isPhysicalPerson={isPhysicalPerson} />
                <StyledProfileName>{currentDetails?.fullName}</StyledProfileName>
                <StyledPolicyholderChip>{t("Common.Policyholder")}</StyledPolicyholderChip>
                <StyledDateActive>
                    {t("User.PersonalDetails.ActiveSince")} <DateFormat date={currentDetails?.activeSince} />
                </StyledDateActive>
            </StyledProfileHeader>
        </ProfileFormPanel>
    );
};

export default PolicyholderCard;
